import { useEffect, useMemo, useRef, useState } from 'react'
import { usePageVisibility } from 'utils/hooks'
import { getBannerContentIndexFromSlide } from './getBannerContentIndexFromSlide'

type useBannerScrollProps = {
  length: number
  visibleCards: number
  infiniteLoop: boolean
  autoScrollIn?: number
  autoScrollEnabled: boolean
  isPopupOpen?: boolean
  onSlideChanged?: (slide: number, contentIndex: number | null) => void
  /** Trigger to programmatically go to next slide (incremented from outside) */
  goToNextSlideTrigger?: number
  /** Restored active slide index for browser back navigation */
  initialSlide?: number
}

const DEFAULT_AUTO_SCROLL_IN = 1500

export const useBannerScroll = ({
  length,
  visibleCards,
  infiniteLoop,
  autoScrollIn,
  autoScrollEnabled,
  isPopupOpen,
  onSlideChanged,
  goToNextSlideTrigger,
  initialSlide,
}: useBannerScrollProps) => {
  const intervalRef = useRef<ReturnType<typeof setTimeout>>(null)

  // Track layout is [leading clones, ...banners, trailing clones], so the first
  // real banner sits at index `visibleCards` (see useBannerElements).
  const [activeSlide, setActiveSlide] = useState<number>(() => {
    if (initialSlide != null) return initialSlide
    return infiniteLoop ? visibleCards : 0
  })

  const isMoreThanOneElement = useMemo(
    () => length > visibleCards,
    [length, visibleCards]
  )

  const isAutoScroll = useMemo(
    () => autoScrollEnabled && isMoreThanOneElement,
    [autoScrollEnabled, isMoreThanOneElement]
  )

  const pageVisibility = usePageVisibility()

  const notifySlideChanged = (slide: number): void => {
    onSlideChanged?.(
      slide,
      getBannerContentIndexFromSlide(slide, length, visibleCards, infiniteLoop)
    )
  }

  const slideNext = (): void => {
    const isLastElement = !infiniteLoop && activeSlide < length
    const isLastElementInInfiniteLoop =
      infiniteLoop && activeSlide < length + visibleCards

    if (
      (isLastElement || isLastElementInInfiniteLoop) &&
      isMoreThanOneElement
    ) {
      const settedSlide = activeSlide + 1
      setActiveSlide(settedSlide)
      notifySlideChanged(settedSlide)
    }
  }

  const stop = (): void => {
    clearTimeout(intervalRef.current as ReturnType<typeof setTimeout>)
  }

  const start = (): void => {
    stop()
    intervalRef.current = setTimeout(() => {
      if (isAutoScroll) slideNext()
    }, autoScrollIn || DEFAULT_AUTO_SCROLL_IN)
  }

  const nextFn = (): void => {
    slideNext()
    if (isAutoScroll) {
      start()
    }
  }

  const slidePrev = (): void => {
    if (activeSlide > 0 && isMoreThanOneElement) {
      const settedSlide = activeSlide - 1
      setActiveSlide(settedSlide)
      notifySlideChanged(settedSlide)
    }
  }

  const prevFn = (): void => {
    slidePrev()
    if (isAutoScroll) {
      start()
    }
  }

  // Jump straight to a banner by its content index (pagination click).
  const goToContentIndex = (contentIndex: number): void => {
    if (!isMoreThanOneElement) return

    const settedSlide = infiniteLoop
      ? contentIndex + visibleCards
      : contentIndex
    if (settedSlide === activeSlide) return

    setActiveSlide(settedSlide)
    notifySlideChanged(settedSlide)

    if (isAutoScroll) {
      start()
    }
  }

  // Start auto scrolling if it's enabled
  useEffect(() => {
    stop()

    start()

    return () => stop()
  }, [activeSlide, isAutoScroll])

  // Stop scrolling when user's mouse move out from page and start after user's mouse come back to page
  useEffect(() => {
    if (pageVisibility && isAutoScroll) start()
    if (!pageVisibility && isAutoScroll) stop()
  }, [length, autoScrollEnabled, visibleCards, pageVisibility])

  // Stop scrolling when infinite loop is disabled and auto scroll is enabled
  // and scroll moved to last element
  useEffect(() => {
    if (autoScrollEnabled && !infiniteLoop && activeSlide === length - 1) stop()
  }, [activeSlide, autoScrollEnabled, infiniteLoop, length])

  // Stop scrolling if popup is opened and start scrolling if popup is closed
  useEffect(() => {
    if (isPopupOpen && isAutoScroll) stop()

    if (!isPopupOpen && isAutoScroll) start()
  }, [isPopupOpen])

  // Go to next slide when trigger changes (programmatic navigation)
  useEffect(() => {
    if (goToNextSlideTrigger && goToNextSlideTrigger > 0) {
      nextFn()
    }
  }, [goToNextSlideTrigger])

  return {
    nextFn,
    prevFn,
    goToContentIndex,
    activeSlide,
    setActiveSlide,
  }
}
